import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import NdaBelgiumHero from './components/NdaBelgiumHero';
import NdaBelgiumContent from './components/NdaBelgiumContent';
import NdaBelgiumClauses from './components/NdaBelgiumClauses';
import NdaBelgiumFaq from './components/NdaBelgiumFaq';
import NdaBelgiumCta from './components/NdaBelgiumCta';
import { faqs } from './components/NdaBelgiumFaq';

const schemaData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://pactorae.com/nda-template-belgium',
      url: 'https://pactorae.com/nda-template-belgium',
      name: 'NDA Template België voor Freelancers | Pactorae',
      description:
        'Gratis NDA template voor Belgische freelancers en zelfstandigen. Professionele geheimhoudingsovereenkomst naar Belgisch recht. Inclusief ondernemingsnummer en btw-nummer.',
      inLanguage: 'nl',
      isPartOf: { '@type': 'WebSite', url: 'https://pactorae.com', name: 'Pactorae' },
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs?.map((faq) => ({
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      })),
    },
  ],
};

const NdaBelgiumLandingPage = () => {
  return (
    <>
      <Helmet>
        <html lang="nl" />
        <title>NDA Template België voor Freelancers en Zelfstandigen | Pactorae</title>
        <meta
          name="description"
          content="Gratis NDA template voor Belgische freelancers en zelfstandigen. Professionele geheimhoudingsovereenkomst naar Belgisch recht. Inclusief ondernemingsnummer en btw-nummer. Genereer direct online."
        />
        <meta
          name="keywords"
          content="NDA template België, geheimhoudingsovereenkomst België, NDA freelancer België, NDA zelfstandige België, geheimhouding overeenkomst België, non disclosure agreement België"
        />
        <link rel="canonical" href="https://pactorae.com/nda-template-belgium" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://pactorae.com/nda-template-belgium" />
        <meta property="og:title" content="NDA Template België voor Freelancers en Zelfstandigen | Pactorae" />
        <meta
          property="og:description"
          content="Gratis NDA template voor Belgische freelancers en zelfstandigen. Professionele geheimhoudingsovereenkomst naar Belgisch recht."
        />
        <meta property="og:site_name" content="Pactorae" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NDA Template België voor Freelancers en Zelfstandigen | Pactorae" />
        <meta
          name="twitter:description"
          content="Gratis NDA template voor Belgische freelancers. Geheimhoudingsovereenkomst naar Belgisch recht met ondernemingsnummer."
        />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <main>
        <NdaBelgiumHero />

        <article className="max-w-4xl mx-auto px-6 py-16">
          {/* Intro */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-4">Wanneer heb je een NDA nodig als Belgische freelancer?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Als zelfstandige of freelancer in België kom je regelmatig in aanraking met vertrouwelijke informatie van opdrachtgevers. Een NDA (Non-Disclosure Agreement) of geheimhoudingsovereenkomst legt juridisch vast welke informatie vertrouwelijk is en wat de gevolgen zijn bij schending. In België wordt de bescherming van bedrijfsgeheimen geregeld door de <strong>Wet van 30 juli 2018</strong>.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Via Pactorae genereer je in enkele minuten een professionele NDA op maat voor jouw Belgische freelance opdracht. De generator vraagt naar jouw <strong>ondernemingsnummer (KBO)</strong> en <strong>btw-nummer</strong> voor correcte identificatie van partijen, en past alle clausules aan op het Belgisch recht.
            </p>
          </section>

          <NdaBelgiumContent />
          <NdaBelgiumClauses />
          <NdaBelgiumFaq />
          <NdaBelgiumCta />

          {/* Internal links */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#2F4E6F] mb-6">Gerelateerde contract templates</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/nda-template-netherlands"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">NDA Nederland →</div>
                <div className="text-gray-600 text-sm">NDA template voor Nederlandse ZZP'ers en freelancers</div>
              </Link>
              <Link
                to="/freelance-contract-belgium"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">Freelance Contract België →</div>
                <div className="text-gray-600 text-sm">Freelance contract template voor Belgische zelfstandigen</div>
              </Link>
              <Link
                to="/freelance-contract-template-benelux"
                className="block p-5 bg-white border border-gray-200 rounded-xl hover:border-[#FF8C00] hover:shadow-md transition-all duration-200"
              >
                <div className="text-[#FF8C00] font-semibold mb-1">Benelux Templates →</div>
                <div className="text-gray-600 text-sm">Contract generator voor België en Nederland</div>
              </Link>
            </div>
          </section>
        </article>
      </main>
    </>
  );
};

export default NdaBelgiumLandingPage;
